/**
 * 方言选择页
 * ============================================================
 *
 * 这一页的核心价值不是"选一个方言"，而是**如实告知能力状态**。
 *
 * 需求文档 V1.0 第 5 节的原则：
 *   「方言目录与模型能力必须分开建模。一个方言可以支持试听和 TTS，
 *     却暂不支持高质量 ASR；UI 应如实显示每项能力。」
 * 第 12 节第一条风险：
 *   「方言支持被高估 —— 建立分模块的能力矩阵。」
 *
 * 所以每张卡片都列出**语音合成 / 语音识别 / 课程内容**三项的独立状态，
 * 并给出实测回识率。哪个方言现在只能听不能练，用户一眼就能看到 ——
 * 不把"可试听"伪装成"全链路可用"。
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchDialects,
  getSelectedDialectId,
  setSelectedDialectId,
  STATUS_STYLE,
} from '../lib/dialects';

export default function DialectSelectPage() {
  const navigate = useNavigate();

  const [dialects, setDialects] = useState([]);
  const [systems, setSystems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [current, setCurrent] = useState(() => getSelectedDialectId());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDialects();
      setDialects(data.dialects || []);
      setSystems(data.systems || []);
      setSummary(data.summary || null);
    } catch (e) {
      console.error('[方言] 加载失败:', e);
      setError(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pick = (id) => {
    setSelectedDialectId(id);
    setCurrent(id);
    // 稍等一下让勾选状态可见，再返回
    setTimeout(() => navigate(-1), 220);
  };

  const shown = filter === 'all' ? dialects : dialects.filter((d) => d.system === filter);

  return (
    <div className="min-h-screen bg-[#fdfaf1] pb-16 dark:bg-zinc-900">
      {/* 顶部 */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-100 bg-white/95 px-5 py-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/95">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-zinc-500 active:opacity-60"
          aria-label="返回"
        >
          <span className="material-icons">arrow_back</span>
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-zinc-800 dark:text-zinc-100">选择方言</h1>
          {summary && (
            <p className="text-[11px] text-zinc-400">
              共 {summary.total} 种 · 目前 {summary.readyForCourse} 种已上线课程
            </p>
          )}
        </div>
      </header>

      <main className="px-5 py-4">
        {/* 体系筛选 */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
            全部
          </Chip>
          {systems.map((s) => (
            <Chip key={s.id} active={filter === s.id} onClick={() => setFilter(s.id)}>
              {s.name}
            </Chip>
          ))}
        </div>

        {loading && (
          <div className="flex justify-center py-14">
            <span className="material-icons animate-spin text-3xl text-zinc-300">refresh</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            加载失败：{error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-3">
            {shown.map((d) => (
              <DialectCard
                key={d.id}
                dialect={d}
                selected={d.id === current}
                onPick={() => pick(d.id)}
              />
            ))}
          </div>
        )}

        {/* 说明：为什么状态要分开标 */}
        <p className="mt-6 text-[11px] leading-relaxed text-zinc-400">
          试用阶段：标为「暂不开放」的方言还没经过母语者确认，暂时不出现在这里；
          母语者试听通过后会重新开放。
          方言的语音合成、语音识别、课程内容是**分别评估**的。
          某个方言可能「能听懂但说不准」，或「能听能说但还没做课程」，
          各项状态如实标注，避免把「可试听」当成「全链路可用」。
        </p>
      </main>
    </div>
  );
}

/* ============================================================
 * 方言卡片
 * ============================================================ */
function DialectCard({ dialect, selected, onPick }) {
  const q = dialect.quality?.roundTrip;
  const qPct = typeof q === 'number' ? Math.round(q * 100) : null;
  const canUse = dialect.tts?.status !== 'unavailable';

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!canUse}
      className={`w-full rounded-2xl border p-4 text-left transition-transform active:scale-[0.99] ${
        selected
          ? 'border-primary bg-primary/5 dark:bg-primary/10'
          : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800'
      } ${canUse ? '' : 'opacity-50'}`}
    >
      <div className="mb-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
            {dialect.displayName || dialect.name}
            {dialect.nativeName && dialect.nativeName !== dialect.name && (
              <span className="ml-2 text-[11px] font-normal text-zinc-400">
                {dialect.nativeName}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-400">
            {dialect.region}
            {dialect.intro ? ` · ${dialect.intro}` : ''}
          </p>
        </div>
        {selected && (
          <span className="material-icons shrink-0 text-primary">check_circle</span>
        )}
      </div>

      {/* 三项能力状态 */}
      <div className="flex flex-wrap gap-1.5">
        {(dialect.badges || []).map((b) => {
          const st = STATUS_STYLE[b.id] || STATUS_STYLE.unavailable;
          return (
            <span
              key={b.key}
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}
              title={b.behavior}
            >
              {b.label}：{st.label}
            </span>
          );
        })}
      </div>

      {/*
        试用评审状态。
        与"能力标签"是两回事：能力标签说"模型理论上能做什么"，
        评审状态说"实测下来能不能用"。试运营期后者更重要 ——
        用户有权知道哪些方言还没经过母语者确认。
      */}
      {dialect.review && dialect.review.verdict !== 'unreviewed' && (
        <div
          className={`mt-2 rounded-lg px-2 py-1.5 text-[10px] leading-relaxed ${
            dialect.review.verdict === 'approved'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : dialect.review.verdict === 'partial'
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
          }`}
        >
          <span className="font-bold">{dialect.review.verdictLabel}</span>
          {dialect.review.reason && <span> · {dialect.review.reason}</span>}
        </div>
      )}

      {/* 实测质量 */}
      {qPct !== null && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] text-zinc-400">实测回识</span>
          <span className="h-1 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-700">
            <span
              className={`block h-full rounded-full ${
                qPct >= 75 ? 'bg-emerald-400' : qPct >= 55 ? 'bg-amber-400' : 'bg-rose-400'
              }`}
              style={{ width: `${qPct}%` }}
            />
          </span>
          <span className="font-mono text-[10px] text-zinc-500">{qPct}%</span>
        </div>
      )}

      {/* 有风险的方言给出明确提示，而不是让用户自己发现 */}
      {dialect.quality?.note && qPct !== null && qPct < 60 && (
        <p className="mt-1.5 text-[10px] leading-relaxed text-amber-600 dark:text-amber-400">
          ⚠️ {dialect.quality.note}
        </p>
      )}
    </button>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition ${
        active
          ? 'bg-primary text-white'
          : 'bg-white text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
      }`}
    >
      {children}
    </button>
  );
}
